import React, { memo, Suspense } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';

import { LoadingSpinner } from '../../layouts';
import { useProfileActions } from '../ProfileMenu/hooks/useProfileActions';
import { DispatchProps, StateProps } from './index';
import {
  CustomerProfilePartial,
  GuestProfilePartial,
  NotLoggedInPartial,
} from './partials';

const AboutMyDataDialog = React.lazy(() => import('../AboutMyDataDialog'));

type OuterProps = {
  params: {
    id?: string;
    type?: 'collection' | 'delivery';
    vrid: string;
  };
};

type Props = DispatchProps & StateProps & OuterProps;

const useStyles = makeStyles((theme: Theme) => ({
  profilePanel: {
    backgroundColor: theme.palette.common.white,
    margin: theme.spacing(2, 0, 3.5),
  },

  loadingState: {
    display: 'flex',
    padding: theme.spacing(3, 0),
  },

  [theme.breakpoints.up('sm')]: {
    profilePanel: {
      margin: theme.spacing(2, 3, 10),
    },
  },

  [theme.breakpoints.up('md')]: {
    profilePanel: {
      margin: theme.spacing(2, 0, 10),
    },
  },

  [theme.breakpoints.up('lg')]: {
    profilePanel: {
      border: `1px solid ${theme.palette.grey[400]}`,
      margin: theme.spacing(2, 0),
      padding: theme.spacing(0, 3),
    },
  },
}));

function ProfilePanelComponent(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    actions,
    collectionSummaries,
    configData,
    contactFormType,
    customer,
    hasDisableNonEssentialCookies,
    hasGdprCookieFeatureFlag,
    intercom,
    languageCode,
    params,
    payments,
    savedCardCount,
    showRestaurantList,
    translate,
  } = props;

  const { customerProfileActions, redirectToLogin, showAboutMyData } =
    useProfileActions(
      actions,
      configData,
      collectionSummaries,
      intercom,
      showRestaurantList,
      params,
    );

  const renderProfilePanel = (): JSX.Element => {
    if (!customer || !customer?.status || customer.status === 'pending') {
      return (
        <LoadingSpinner className={classes.loadingState} variant="inline" />
      );
    }

    if (customer.status === 'success') {
      if (customer.data.IsGuest) {
        return (
          <GuestProfilePartial
            contactFormType={contactFormType}
            onContactUs={customerProfileActions.onContactUs}
            onLogin={redirectToLogin}
            translate={translate}
          />
        );
      }

      return (
        <>
          <CustomerProfilePartial
            actions={customerProfileActions}
            configData={configData}
            contactFormType={contactFormType}
            customerData={customer.data}
            hasDisableNonEssentialCookies={hasDisableNonEssentialCookies}
            hasGdprCookieFeatureFlag={hasGdprCookieFeatureFlag}
            languageCode={languageCode}
            payments={payments}
            savedCardCount={savedCardCount}
            translate={translate}
          />

          {showAboutMyData && (
            <Suspense fallback={<LoadingSpinner />}>
              <AboutMyDataDialog
                handleOnClick={customerProfileActions.onAboutMyData}
                showDialog={showAboutMyData}
                translate={translate}
              />
            </Suspense>
          )}
        </>
      );
    }

    return (
      <NotLoggedInPartial
        contactFormType={contactFormType}
        onContactUs={customerProfileActions.onContactUs}
        onLogin={redirectToLogin}
        translate={translate}
      />
    );
  };

  const profilePanelStyles = clsx(
    'fd-section__profile-panel',
    classes.profilePanel,
  );

  return <div className={profilePanelStyles}>{renderProfilePanel()}</div>;
}

const ProfilePanel = memo(ProfilePanelComponent);
ProfilePanel.displayName = 'ProfilePanel';

export { ProfilePanel };
